/**
 * RobotConnectionManagerContext — Single React provider for all WebRTC connections to a robot.
 *
 * Replaces N nested RobotPeerConnectionProviders with one provider per robot.
 * Creates a ConnectionManager, gives it a signaling bridge over the server
 * WebSocket, and exposes it via context. Signaling events (SDP answer, ICE
 * candidates, robot online/offline) are routed by the ConnectionManager itself,
 * so no React re-render is needed between a peer being created and its
 * signaling being wired up.
 */

import React, { createContext, useContext, useEffect, useRef, useState, useCallback, useMemo } from "react";
import { ConnectionManager } from "features/robot-teleop/webrtc/ConnectionManager";
import { useEventBus } from "shared/services/eventBus";
import { useServerConnection } from "shared/contexts/ServerConnectionContext";
import { CONNECTION_EVENTS, ROBOT_EVENTS, getRobotEvent } from "shared/services/eventBus/eventTypes";
import { CONNECTION_STATES } from "shared/utils/stateMachine/ConnectionStateMachine";
import logger from "shared/utils/logging";

const RobotConnectionManagerContext = createContext(null);

const sameIds = (a, b) => a.length === b.length && a.every((id, i) => id === b[i]);

export const RobotConnectionManagerProvider = ({
  children,
  robotId,
  trickleICE = true,
  dataChannelsConfig,
  iceConfig,
}) => {
  const eventBus = useEventBus();
  const { sendMessage, sendRequestMessage, connectionState: serverState, CONNECTION_STATES: SERVER_STATES } = useServerConnection();

  // Latest signaling functions and per-manager config, read lazily so that an
  // identity change of a callback or an inline config object never destroys
  // and recreates the manager (which would drop every live connection).
  const sendMessageRef = useRef(sendMessage);
  const sendRequestMessageRef = useRef(sendRequestMessage);
  sendMessageRef.current = sendMessage;
  sendRequestMessageRef.current = sendRequestMessage;
  // An offer sent before the socket is authenticated is silently dropped by
  // the server and the request would only fail after the 30 s timeout.
  const signalingReadyRef = useRef(false);
  signalingReadyRef.current = serverState === SERVER_STATES.CONNECTED;
  const dataChannelsConfigRef = useRef(dataChannelsConfig);
  dataChannelsConfigRef.current = dataChannelsConfig;
  const iceConfigRef = useRef(iceConfig);
  iceConfigRef.current = iceConfig;

  const managerRef = useRef(null);
  const [manager, setManager] = useState(null);
  const [overallState, setOverallState] = useState('disconnected');
  const [peerIds, setPeerIds] = useState([]);
  const [controlPeerId, setControlPeerId] = useState(null);

  // Build the signaling bridge — preserves existing signaling protocol
  const signalingBridge = useMemo(() => ({
    sendOffer: (peerId, sdpObj) => {
      if (!signalingReadyRef.current) {
        return Promise.reject(new Error("Signaling server is not connected"));
      }
      const data = { RobotId: peerId, sdp: sdpObj.sdp, type: sdpObj.type };
      // attempt id: forwarded by conn-manager and echoed by the robot in its
      // answer so that PeerConnection can drop answers of superseded offers
      if (sdpObj.attempt != null) data.attempt = sdpObj.attempt;
      const msg = JSON.stringify({ cmd: "connect2robot", data });
      return sendRequestMessageRef.current(msg);
    },

    sendIceCandidate: (peerId, candidate) => {
      const msg = JSON.stringify({
        cmd: "signal",
        data: { type: "candidate", candidate, RobotId: peerId },
      });
      return Promise.resolve()
        .then(() => sendMessageRef.current(msg))
        .catch((err) => logger.warn(`[RobotConnectionManager] Failed to send ICE candidate to ${peerId}:`, err));
    },

    sendMediaConfig: (peerId, config) => {
      const msg = JSON.stringify({
        cmd: "signal",
        data: { type: "media_config", ...config, RobotId: peerId },
      });
      return sendRequestMessageRef.current(msg);
    },

    // Fire-and-forget media_config. conn-manager forwards a plain `signal` to
    // the robot's signal handler, which applies audio and video config; the
    // request above goes through the robot's command path, which only knows
    // video config.
    sendMediaSignal: (peerId, config) => {
      const msg = JSON.stringify({
        cmd: "signal",
        data: { type: "media_config", ...config, RobotId: peerId },
      });
      return Promise.resolve().then(() => sendMessageRef.current(msg));
    },

    // Fire-and-forget: the server forwards it as 'user_disconnected' to the
    // robot, which drops our peer (and its camera track) immediately instead of
    // waiting for its ICE consent checks to expire.
    sendDisconnect: (peerId) => {
      const msg = JSON.stringify({
        cmd: "disconnect2robot",
        data: { RobotId: peerId },
      });
      return Promise.resolve()
        .then(() => sendMessageRef.current(msg))
        .catch((err) => logger.debug(`[RobotConnectionManager] Failed to send disconnect to ${peerId}:`, err));
    },

    sendEvent: (peerId, eventType, eventData) => {
      const msg = JSON.stringify({
        cmd: "event",
        data: { event_type: eventType, event_data: eventData },
      });
      return Promise.resolve().then(() => sendMessageRef.current(msg));
    },
  }), []);

  // Create/recreate manager when robotId changes
  useEffect(() => {
    if (!robotId) return undefined;

    logger.debug(`[RobotConnectionManager] Initializing manager for robot ${robotId}`);
    const newManager = new ConnectionManager(robotId, {
      eventBus,
      signalingBridge,
      trickleICE,
      dataChannelsConfig: dataChannelsConfigRef.current,
      iceConfig: iceConfigRef.current,
    });
    managerRef.current = newManager;
    setManager(newManager);
    setOverallState('disconnected');
    setPeerIds([]);
    setControlPeerId(null);

    return () => {
      logger.debug(`[RobotConnectionManager] Destroying manager for robot ${robotId}`);
      newManager.destroy();
      if (managerRef.current === newManager) managerRef.current = null;
      setManager((current) => (current === newManager ? null : current));
      setOverallState('disconnected');
      setPeerIds([]);
      setControlPeerId(null);
    };
  }, [robotId, eventBus, signalingBridge, trickleICE]);

  // Listen for server reconnection → reconnect peers that are not connected
  useEffect(() => {
    const unsub = eventBus.subscribe(
      CONNECTION_EVENTS.SERVER_STATE_CHANGED,
      ({ state }) => {
        if (state === CONNECTION_STATES.CONNECTED) {
          logger.log("[RobotConnectionManager] Signaling server connected, reconnecting peers");
          managerRef.current?.reconnectAll();
        }
      }
    );
    return unsub;
  }, [eventBus]);

  // Update overall state when any peer's connection state changes
  useEffect(() => {
    if (!manager || peerIds.length === 0) return undefined;

    const refresh = () => {
      if (managerRef.current === manager) {
        setOverallState(manager.getOverallState());
      }
    };
    refresh();
    const subs = peerIds.map((peerId) =>
      eventBus.subscribe(getRobotEvent(peerId, ROBOT_EVENTS.CONNECTION_STATE), refresh)
    );

    return () => subs.forEach((unsub) => { if (typeof unsub === 'function') unsub(); });
  }, [manager, peerIds, eventBus]);

  // ==================== Public API ====================

  const initialize = useCallback((peerInfos, connectConfig) => {
    const current = managerRef.current;
    if (!current) return;
    current.initialize(peerInfos, connectConfig);
    const ids = current.getAllPeers().map((p) => p.peerId);
    setPeerIds((prev) => (sameIds(prev, ids) ? prev : ids));
    setControlPeerId(current.controlPeerId);
    setOverallState(current.getOverallState());
  }, []);

  const connectAll = useCallback(async () => {
    await managerRef.current?.connectAll();
  }, []);

  const connectAutoConnectPeers = useCallback(async () => {
    await managerRef.current?.connectAutoConnectPeers();
  }, []);

  const connectPeer = useCallback(async (peerId) => {
    await managerRef.current?.connectPeer(peerId);
  }, []);

  const disconnectAll = useCallback(() => {
    managerRef.current?.disconnectAll();
  }, []);

  const disconnectPeer = useCallback((peerId) => {
    managerRef.current?.disconnectPeer(peerId);
  }, []);

  const reconnectFailed = useCallback(async () => {
    await managerRef.current?.reconnectFailed();
  }, []);

  const setAutoConnect = useCallback((peerId, enabled) => {
    managerRef.current?.setAutoConnect(peerId, enabled);
  }, []);

  const getPeer = useCallback((peerId) => managerRef.current?.getPeer(peerId), []);

  const value = useMemo(() => ({
    manager,
    robotId,
    overallState,
    peerIds,
    controlPeerId,
    initialize,
    connectAll,
    connectAutoConnectPeers,
    connectPeer,
    disconnectAll,
    disconnectPeer,
    reconnectFailed,
    setAutoConnect,
    getPeer,
  }), [
    manager, robotId, overallState, peerIds, controlPeerId,
    initialize, connectAll, connectAutoConnectPeers, connectPeer,
    disconnectAll, disconnectPeer, reconnectFailed, setAutoConnect, getPeer,
  ]);

  return (
    <RobotConnectionManagerContext.Provider value={value}>
      {children}
    </RobotConnectionManagerContext.Provider>
  );
};

/**
 * Hook to access the connection manager context.
 */
export const useConnectionManager = () => {
  const ctx = useContext(RobotConnectionManagerContext);
  if (!ctx) {
    throw new Error("useConnectionManager must be used within RobotConnectionManagerProvider");
  }
  return ctx;
};

export default RobotConnectionManagerContext;
